import React, { useContext, useMemo, useState } from 'react';

import { Box, Fab, Stack, Tab, Tabs, Typography } from '@mui/material';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

import { TypoPoppins } from '../../Components/Typo';

import { ORDER_STATUS } from '../../Constant/Enums';

import ToastContext from '../../Context/ToastContext';
import { useAuthContext } from '../../Context/AuthContext';

import Layout from './Components/Layout';
import useUserOrders from './Components/useUserOrders';
import JobInformationCard from './Components/JobInformationCard';

const JobInformation = () => {
  // const { user } = useAuthContext();
  const { showToastMessage } = useContext(ToastContext);

  const [selectedTab, setSelectedTab] = useState(TABS.COMMITTED);

  const {
    orders: committedOrders,
    updateOrders: updateCommittedOrders,
    hasErrored: committedHasErrored,
  } = useUserOrders(
    // user.userID,
    // useMemo(() => [ORDER_STATUS.ACCEPTED], [])
  );

  const { orders: cancelledOrCompletedOrders, hasErrored: cancelledOrCompletedHasErrored } =
    useUserOrders(
      // user.userID,
      // useMemo(() => [ORDER_STATUS.CANCELLED, ORDER_STATUS.COMPLETED, ORDER_STATUS.REJECTED], [])
    );

  const [isCommittedScrollTopVisible, setIsCommittedScrollTopVisible] = useState(false);
  const [isCompletedOrRejectedScrollTopVisible, setIsCompletedOrRejectedScrollTopVisible] =
    useState(false);

  function handleTabChange(_event, value) {
    setSelectedTab(value);
  }

  function handleScrollToTop(event) {
    event.currentTarget.parentElement?.scroll({
      top: 0,
      behavior: 'smooth',
    });
  }

  function handleCommittedScrollTopVisibility(event) {
    const shouldScrollTopVisible = event.currentTarget.scrollTop > 100;
    if (shouldScrollTopVisible !== isCommittedScrollTopVisible)
      setIsCommittedScrollTopVisible(shouldScrollTopVisible);
  }

  function handleCompletedScrollTopVisibility(event) {
    const shouldScrollTopVisible = event.currentTarget.scrollTop > 100;
    if (shouldScrollTopVisible !== isCompletedOrRejectedScrollTopVisible)
      setIsCompletedOrRejectedScrollTopVisible(shouldScrollTopVisible);
  }

  if (committedHasErrored || cancelledOrCompletedHasErrored)
    showToastMessage('Unable to load data. Please refresh the page and try again.');

  return (
    <Layout fullWidth>
      <Box
        display={{ xs: 'flex', md: 'initial' }}
        flexDirection={{ xs: 'column', md: 'initial' }}
        alignItems={{ xs: 'center', md: 'initial' }}
        justifyContent={{ xs: 'center', md: 'initial' }}
        flexGrow={{ xs: '1', md: 'initial' }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          sx={{
            mb: 2,
            position: 'relative',
            borderRadius: '4px',
          }}
          TabIndicatorProps={{
            sx: ({ palette }) => ({
              borderRadius: '4px',
              zIndex: '-1',
              height: '100%',
              position: 'absolute',
              bgcolor: palette.white3.main,
            }),
          }}>
          <Tab
            disableRipple
            label={<TypoPoppins textTransform='none'>Committed for Job</TypoPoppins>}
            value={TABS.COMMITTED}
          />
          <Tab
            disableRipple
            label={<TypoPoppins textTransform='none'>Complete or Reject</TypoPoppins>}
            value={TABS.COMPLETED_OR_REJECTED}
          />
        </Tabs>

        <TabPanel value={TABS.COMMITTED} selectedTab={selectedTab}>
          {committedOrders?.length ? (
            <div style={{ position: 'relative' }}>
              <Stack
                gap={2}
                maxHeight='500px'
                overflow='auto'
                px={{ xs: 1, md: 'unset' }}
                pr={{ md: 2 }}
                pb={4}
                onScroll={handleCommittedScrollTopVisibility}>
                {committedOrders.map(cardProps => (
                  <div style={{ flexShrink: 0, maxWidth: '600px' }} key={cardProps.id}>
                    <JobInformationCard
                      data={cardProps}
                      updateParentCards={updateCommittedOrders}
                    />
                  </div>
                ))}
                {isCommittedScrollTopVisible && (
                  <Fab
                    size='small'
                    sx={{
                      position: 'absolute',
                      right: '16px',
                      bottom: '8px',
                    }}
                    onClick={handleScrollToTop}>
                    <ArrowUpwardIcon />
                  </Fab>
                )}
              </Stack>
            </div>
          ) : (
            <Typography color='text.secondary' variant='h6'>
              No committed jobs available
            </Typography>
          )}
        </TabPanel>

        <TabPanel value={TABS.COMPLETED_OR_REJECTED} selectedTab={selectedTab}>
          {cancelledOrCompletedOrders.length ? (
            <div style={{ position: 'relative' }}>
              <Stack
                gap={2}
                direction='column'
                maxHeight='500px'
                overflow='auto'
                px={{ xs: 1, md: 'unset' }}
                pr={{ md: 2 }}
                pb={4}
                onScroll={handleCompletedScrollTopVisibility}>
                {cancelledOrCompletedOrders.map(cardProps => (
                  <div style={{ flexShrink: 0, maxWidth: '600px' }} key={cardProps.id}>
                    <JobInformationCard data={cardProps} />
                  </div>
                ))}
                {isCompletedOrRejectedScrollTopVisible && (
                  <Fab
                    size='small'
                    onClick={handleScrollToTop}
                    sx={{
                      position: 'absolute',
                      right: '16px',
                      bottom: '8px',
                    }}>
                    <ArrowUpwardIcon />
                  </Fab>
                )}
              </Stack>
            </div>
          ) : (
            <Typography color='text.secondary' variant='h6'>
              No completed or rejected jobs available
            </Typography>
          )}
        </TabPanel>
      </Box>
    </Layout>
  );
};

const TABS = Object.freeze({
  COMMITTED: 'COMMITTED',
  COMPLETED_OR_REJECTED: 'COMPLETED_OR_REJECTED',
});

function TabPanel({ value, selectedTab, children }) {
  if (value !== selectedTab) return null;

  return children;
}

export default JobInformation;
